import React, { useContext, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router";

import { ApiHelper } from "@datanac/datanac-api-toolkit/dist/api-helper";
import { useDialog } from "@datanac/datanac-mui-toolkit";
import { Box, Chip, Divider, Grid, Skeleton, Stack, Typography } from "@mui/material";
import { useArcCoCountyYieldOverrides } from "api-toolkit/useArcCoCountyYieldOverrides";
import { findArcCoCountyYieldOverrideForBaseAcre, normalizeArcPlcRouteTypeName, useArcPlc } from "api-toolkit/useArcPlc";
import { _sessionManager, UsersApiHelper } from "api/ApiHelper";
import { AppContext } from "App";
import { ArcPlcIcon, InfoIcon } from "components/Icons/Icons";
import WorkspaceHeader from "components/Workspaces/WorkspaceHeader";
import { SkeletonWrapper } from "mui-toolkit/SkeletonWrapper";
import { useSnackbar } from "notistack";
import { formatNumber, formatPrice } from "utility/formatters";

import { BUDGET_STATUS_ACTIVE } from "../Budget/helpers";
import { TilleyArcPlcCard } from "../Cards/TilleyArcPlcCard";
import {
  ArcPlcService,
  MARKET_YEAR_AVERAGE_PRICE_STATUS_PROJECTED,
  PRACTICE_NO_PRACTICE_SPECIFIED,
} from "./ArcPlcService";

export default function ArcPlcBaseAcreWorkspace() {
  const { globalState } = useContext(AppContext);

  const {
    crop_year,
    location_state_name,
    location_county_name,
    commodity_name,
    type_name,
    producer_farm_base_acres_id,
  } = useParams();

  const { enqueueSnackbar } = useSnackbar();
  const { component: confirmDialogComponent } = useDialog();

  const [baseAcres, setBaseAcres] = useState(null);
  const { arcCoCountyYieldOverrides } = useArcCoCountyYieldOverrides({ crop_year });
  const [arcCoCountyYieldOverride, setArcCoCountyYieldOverride] = useState(null);

  const rowOverride = useMemo(
    () =>
      baseAcres?.[0] ? findArcCoCountyYieldOverrideForBaseAcre(baseAcres[0], arcCoCountyYieldOverrides) : null,
    [baseAcres, arcCoCountyYieldOverrides]
  );

  // Same county/crop override record as ArcPlcCountyBaseAcreWorkspace (fallback if row match misses)
  const effectiveOverride = useMemo(
    () => ({
      final_county_yield_irrigated:
        arcCoCountyYieldOverride?.final_county_yield_irrigated ?? rowOverride?.final_county_yield_irrigated,
      final_county_yield_non_irrigated:
        arcCoCountyYieldOverride?.final_county_yield_non_irrigated ?? rowOverride?.final_county_yield_non_irrigated,
      market_year_average_price:
        arcCoCountyYieldOverride?.market_year_average_price ?? rowOverride?.market_year_average_price,
    }),
    [rowOverride, arcCoCountyYieldOverride]
  );

  const arcPlc = useArcPlc({
    baseAcres,
    crop_year,
    location_state_name,
    location_county_name,
    commodity_name,
    type_name: normalizeArcPlcRouteTypeName(type_name),
    arcCoCountyYieldOverrides,
    countyYieldOverrideRecord: arcCoCountyYieldOverride ?? rowOverride,
    final_county_yield_irrigated: effectiveOverride.final_county_yield_irrigated,
    final_county_yield_non_irrigated: effectiveOverride.final_county_yield_non_irrigated,
    market_year_average_price: effectiveOverride.market_year_average_price,
  });

  const handleError = (err) => {
    console.warn(err);
    enqueueSnackbar(err.detail || err.message || JSON.stringify(err), { preventDuplicate: true, variant: "error" });
  };

  const loadBaseAcres = async () => {
    try {
      const baseAcresFilter = {
        producer_token: globalState.producer_token,
        location_state_name,
        location_county_name,
        id: producer_farm_base_acres_id,
        crop_year: crop_year,
        is_active: true,
      };
      const _baseAcres = await UsersApiHelper.selectProducerFarmBaseAcres(baseAcresFilter, globalState);
      if (_baseAcres?.length) {
        setBaseAcres(_baseAcres);
      } else {
        setBaseAcres(null);
      }
    } catch (err) {
      setBaseAcres(null);
      handleError(err);
    }
  };

  useEffect(() => {
    if (producer_farm_base_acres_id && globalState?.producer_token) {
      loadBaseAcres();
    }
  }, [producer_farm_base_acres_id, globalState?.producer_token]);

  const loadArcCoCountyYieldOverride = async () => {
    if (!location_state_name || !location_county_name || !commodity_name || !crop_year || !globalState?.producer_token) {
      setArcCoCountyYieldOverride(null);
      return;
    }

    try {
      const api = new ApiHelper({
        baseUrl: import.meta.env.VITE_DATANAC_USERS_API_URL_BASE,
        config: {},
        sessionManager: _sessionManager,
        _instance: null,
      });
      const overrides = await api.callPost(
        "ArcCoCountyYieldOverride/search",
        {
          producer_token: globalState.producer_token,
          state_name: location_state_name,
          county_name: location_county_name,
          commodity_name,
          type_name: normalizeArcPlcRouteTypeName(type_name),
          crop_year,
          is_active: true,
        },
        globalState
      );
      setArcCoCountyYieldOverride(overrides?.length ? overrides[0] : null);
    } catch (err) {
      setArcCoCountyYieldOverride(null);
      handleError(err);
    }
  };

  useEffect(() => {
    loadArcCoCountyYieldOverride();
  }, [
    location_state_name,
    location_county_name,
    commodity_name,
    type_name,
    crop_year,
    globalState?.producer_token,
  ]);

  // ---

  const [isLoadingPLCPaymentRates, setIsLoadingPLCPaymentRates] = useState(true);
  useEffect(() => {
    if (baseAcres?.length && globalState?.producer_token) {
      setIsLoadingPLCPaymentRates(false);
    }
  }, [baseAcres, globalState?.producer_token]);

  // ---

  const [EffectiveReferencePrices, setEffectiveReferencePrices] = useState(null);

  const loadEffectiveReferencePrices = async () => {
    try {
      const { actions: arcPlcActions } = new ArcPlcService({
        apiConfig: globalState,

        crop_year: crop_year,
        location_state_name,
        location_county_name,

        baseAcres: baseAcres,
      });

      const _EffectiveReferencePrices = await arcPlcActions.getEffectiveReferencePrices();
      if (_EffectiveReferencePrices?.length) {
        setEffectiveReferencePrices(_EffectiveReferencePrices[0]?.EffectiveReferencePrices);
      }
    } catch (err) {
      setEffectiveReferencePrices(null);
      handleError(err);
    }
  };

  useEffect(() => {
    if (baseAcres?.length && globalState?.producer_token) {
      loadEffectiveReferencePrices();
    }
  }, [baseAcres, globalState?.producer_token]);

  // ---

  const [isLoadingARCCOPrices, setIsLoadingARCCOPrices] = useState(true);

  useEffect(() => {
    if (baseAcres?.length && globalState?.producer_token) {
      setIsLoadingARCCOPrices(false);
    }
  }, [baseAcres, globalState?.producer_token]);

  // ---

  const [marketYearAveragePrices, setMarketYearAveragePrices] = useState(null);

  const loadMarketYearAveragePrices = async () => {
    try {
      const { actions: arcPlcActions } = new ArcPlcService({
        apiConfig: globalState,

        crop_year: crop_year,
        location_state_name,
        location_county_name,

        baseAcres: baseAcres,
      });

      const _marketYearAveragePrices = await arcPlcActions.getMarketYearAveragePrices();
      if (_marketYearAveragePrices?.length && _marketYearAveragePrices[0]?.MarketYearAveragePrices?.length) {
        setMarketYearAveragePrices(_marketYearAveragePrices[0]?.MarketYearAveragePrices[0]);
      }
    } catch (err) {
      setMarketYearAveragePrices(null);
      handleError(err);
    }
  };

  useEffect(() => {
    if (baseAcres?.length && globalState?.producer_token) {
      loadMarketYearAveragePrices();
    }
  }, [baseAcres, globalState?.producer_token]);

  // ---

  const [ARCCOYields, setARCCOYields] = useState(null);

  const loadARCCOYields = async () => {
    try {
      const { actions: arcPlcActions } = new ArcPlcService({
        apiConfig: globalState,

        crop_year: crop_year,
        location_state_name,
        location_county_name,

        baseAcres: baseAcres,
      });

      const _arccoYields = await arcPlcActions.getARCCOYields();
      if (_arccoYields?.length) {
        setARCCOYields(_arccoYields[0]);
      }
    } catch (err) {
      setARCCOYields(null);
      handleError(err);
    }
  };

  useEffect(() => {
    if (baseAcres?.length && globalState?.producer_token) {
      loadARCCOYields();
    }
  }, [baseAcres, globalState?.producer_token]);

  const displayMarketYearAveragePrice =
    effectiveOverride.market_year_average_price ?? marketYearAveragePrices?.price;

  // --- --- ---

  const [producerFarm, setProducerFarm] = useState({});
  const loadProducerFarm = async () => {
    try {
      if (baseAcres && baseAcres?.length && baseAcres[0].producer_farm_id) {
        const producerFilter = {
          producer_token: globalState.producer_token,
          id: baseAcres[0].producer_farm_id,
          is_active: true,
        };
        const farms = await UsersApiHelper.selectProducerFarms(producerFilter, globalState);
        if (farms?.length) {
          setProducerFarm(farms[0]);
        }
      }
    } catch (err) {
      setProducerFarm({});
      handleError(err);
    }
  };

  useEffect(() => {
    if (baseAcres?.length && globalState?.producer_token) {
      loadProducerFarm();
    }
  }, [baseAcres?.length, globalState?.producer_token]);

  // --- --- ---

  const [budgetCountyPractices, setBudgetCountyPractices] = useState([]);
  const loadBudgetCountyPractices = async () => {
    const _budgetFilter = {
      state_name: location_state_name,
      county_name: location_county_name,
      commodity_name: commodity_name,
      year: crop_year,
      status: BUDGET_STATUS_ACTIVE,
      is_active: true,
    };

    try {
      const _budgetCountyPractices = await UsersApiHelper.selectBudgetCountyPractices(_budgetFilter, globalState);
      setBudgetCountyPractices(_budgetCountyPractices);
    } catch (err) {
      setBudgetCountyPractices([]);
      handleError(err);
    }
  };

  useEffect(() => {
    if (location_state_name && location_county_name && commodity_name && crop_year && globalState?.producer_token) {
      loadBudgetCountyPractices();
    }
  }, [location_state_name, location_county_name, commodity_name, crop_year, globalState?.producer_token]);

  // --- --- ---

  return (
    <>
      <WorkspaceHeader
        title={
          !(producerFarm?.fsa_farm_number && baseAcres?.length)
            ? "ARC/PLC"
            : producerFarm?.fsa_farm_number +
              (producerFarm?.farm_name ? " - " + producerFarm?.farm_name : "") +
              (" - " + (baseAcres?.[0]?.commodity_name || ""))
        }
        category={{
          title: "ARC/PLC",
          icon: <ArcPlcIcon />,
        }}
        breadCrumbs={[
          {
            title: "ARC/PLC " + crop_year,
            path: "/arcplc",
          },
          {
            title: location_county_name + ", " + location_state_name,
            path: `/arcplc/${crop_year}/${location_state_name}/${location_county_name}`,
          },
          {
            title: commodity_name,
            path: `/arcplc/${crop_year}/${location_state_name}/${location_county_name}/${commodity_name}`,
          },
        ]}
      />
      <Typography variant="h1">Program Comparison</Typography>

      <Grid container spacing={2} className="base-acres">
        <Grid item xs={12} md={6} lg={4} xl={3}>
          <TilleyArcPlcCard
            customContent={
              <>
                <Stack
                  direction="column"
                  className="custom-content"
                  sx={{
                    height: "100%",
                    justifyContent: "space-between",
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "center",
                      alignItems: "center",
                      flexGrow: 1,
                    }}
                  >
                    {!arcPlc?.isLoading && (
                      <>
                        {arcPlc?.totals?.arcPayment === null && <Typography variant="h1">--</Typography>}
                        {Math.round(arcPlc?.totals?.arcPayment) === 0 && (
                          <Chip
                            label={
                              <Stack direction="column" alignItems="center" spacing={0.5}>
                                <InfoIcon />
                                <Typography variant="caption" align="center">
                                  No ARC-CO payment projected
                                </Typography>
                              </Stack>
                            }
                          />
                        )}
                        {Math.round(arcPlc?.totals?.arcPayment) > 0 && (
                          <Typography variant="h1" className="price">
                            {formatPrice(arcPlc?.totals?.arcPayment, 0)}
                          </Typography>
                        )}
                      </>
                    )}
                    {arcPlc?.isLoading && (
                      <Typography variant="h1" className="skeleton">
                        <Skeleton variant="rectangular" width={150} height={50} sx={{ bgcolor: "text.secondary" }} />
                      </Typography>
                    )}
                  </Box>
                </Stack>
                <Typography variant="body1" align="center">
                  ARC-CO
                  <br />
                  PAYMENT
                </Typography>
              </>
            }
          />
        </Grid>

        <Grid item xs={12} md={6} lg={4} xl={3}>
          <TilleyArcPlcCard
            customContent={
              <>
                <Stack
                  direction="column"
                  className="custom-content"
                  sx={{
                    height: "100%",
                    justifyContent: "space-between",
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "center",
                      alignItems: "center",
                      flexGrow: 1,
                    }}
                  >
                    {!arcPlc?.isLoading && (
                      <>
                        {arcPlc?.totals?.plcPayment === null && <Typography variant="h1">--</Typography>}
                        {Math.round(arcPlc?.totals?.plcPayment) === 0 && (
                          <Chip
                            label={
                              <Stack direction="column" alignItems="center" spacing={0.5}>
                                <InfoIcon />
                                <Typography variant="caption" align="center">
                                  No PLC payment projected
                                </Typography>
                              </Stack>
                            }
                          />
                        )}
                        {Math.round(arcPlc?.totals?.plcPayment) > 0 && (
                          <Typography variant="h1" className="price">
                            {formatPrice(arcPlc?.totals?.plcPayment, 0)}
                          </Typography>
                        )}
                      </>
                    )}
                    {arcPlc?.isLoading && (
                      <Typography variant="h1" className="skeleton">
                        <Skeleton variant="rectangular" width={150} height={50} sx={{ bgcolor: "text.secondary" }} />
                      </Typography>
                    )}
                  </Box>
                </Stack>
                <Typography variant="body1">
                  PLC
                  <br />
                  PAYMENT
                </Typography>
              </>
            }
          />
        </Grid>
      </Grid>

      <Divider />

      <Typography variant="h1">Based On</Typography>
      <Stack direction="row" spacing={1}>
        <Stack direction="column">
          <SkeletonWrapper isLoading={isLoadingARCCOPrices}>
            <Typography variant="h1" sx={{ whiteSpace: "pre" }}>
              {formatPrice(displayMarketYearAveragePrice, 4)}
            </Typography>
          </SkeletonWrapper>
          <Typography variant="body1">
            MYA
            {effectiveOverride.market_year_average_price == null &&
              marketYearAveragePrices?.price_status === MARKET_YEAR_AVERAGE_PRICE_STATUS_PROJECTED && (
              <>
                <br />
                (PROJECTED)
              </>
            )}
          </Typography>
        </Stack>

        <Divider orientation="vertical" flexItem />

        <Stack direction="column">
          <Typography variant="h1">
            {!arcPlc?.isLoading && <>{formatNumber(arcPlc?.totals?.base_acreage, 0)}</>}
            {arcPlc?.isLoading && (
              <>
                <Skeleton />
              </>
            )}
          </Typography>
          <Typography variant="body1">BASE ACRES</Typography>
        </Stack>
      </Stack>

      <Divider />

      <Typography variant="h1">PLC</Typography>
      <Stack direction="row" spacing={1}>
        <Stack direction="column">
          <SkeletonWrapper isLoading={isLoadingPLCPaymentRates}>
            <Typography variant="h1" sx={{ whiteSpace: "pre" }}>
              {formatPrice(
                EffectiveReferencePrices?.length && EffectiveReferencePrices[0]?.effective_reference_price,
                4
              )}
            </Typography>
          </SkeletonWrapper>
          <Typography variant="body1">ERP PRICE</Typography>
        </Stack>

        <Divider orientation="vertical" flexItem />

        <Stack direction="column">
          <Typography variant="h1">
            {!arcPlc?.isLoading && <>{formatNumber(arcPlc?.totals?.plc_yield, 0)}</>}
            {arcPlc?.isLoading && (
              <>
                <Skeleton />
              </>
            )}
          </Typography>
          <Typography variant="body1">PLC YIELD</Typography>
        </Stack>
      </Stack>

      <Divider />

      <Typography variant="h1">ARC-CO</Typography>
      <Stack flexWrap="wrap" direction="row" columnGap={1} rowGap={1}>
        <Stack direction="column">
          <Typography variant="h1" sx={{ whiteSpace: "pre" }}>
            {formatPrice(ARCCOYields && ARCCOYields?.benchmark_price, 4)}
          </Typography>
          <Typography variant="body1">BENCHMARK PRICE</Typography>
        </Stack>

        <Divider orientation="vertical" flexItem />

        <Stack direction="column">
          <Typography variant="h1" sx={{ whiteSpace: "pre" }}>
            {formatNumber(ARCCOYields && ARCCOYields?.benchmark_yield, 1)}
          </Typography>
          <Typography variant="body1">BENCHMARK YIELD</Typography>
        </Stack>

        <Divider orientation="vertical" flexItem />

        <Stack direction="column">
          <Typography variant="h1" sx={{ whiteSpace: "pre" }}>
            {formatPrice(arcPlc?.totals && arcPlc?.totals?.benchmark_guarantee, 2)}
          </Typography>
          <Typography variant="body1">GUARANTEE</Typography>
        </Stack>

        {Boolean(ARCCOYields?.length) &&
          ARCCOYields?.map((arc, index) => (
            <React.Fragment key={index}>
              <Divider orientation="vertical" flexItem />
              <Stack direction="column">
                <Typography variant="h1">{formatNumber(arc?.benchmark_yield, 2)}</Typography>
                <Typography variant="body1" sx={{ textTransform: "uppercase" }}>
                  {arc?.practice_name !== PRACTICE_NO_PRACTICE_SPECIFIED && (
                    <>
                      {arc?.practice_name}
                      <br />
                    </>
                  )}
                  YIELD
                </Typography>
              </Stack>
              <Divider orientation="vertical" flexItem />
              <Stack direction="column">
                <Typography variant="h1" sx={{ whiteSpace: "pre" }}>
                  {formatPrice(arc?.guarantee_revenue, 4)}
                </Typography>
                <Typography variant="body1" sx={{ textTransform: "uppercase" }}>
                  {arc?.practice_name !== PRACTICE_NO_PRACTICE_SPECIFIED && (
                    <>
                      {arc?.practice_name}
                      <br />
                    </>
                  )}
                  GUARANTEE /ac
                </Typography>
              </Stack>
            </React.Fragment>
          ))}
      </Stack>

      {confirmDialogComponent}
    </>
  );
}
